import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewport } from "src/common/hooks/ViewPort";
import ListRow from "src/components/atoms/ListRow/ListRow";
import { confirmDialog } from "src/components/containers/ConfirmDialog";
import { IconBox } from "src/pages/register/Register.style";
import { ReactComponent as IconCamera } from "../../../styles/assets/svg/icon_defaut_camera.svg";
import { ReactComponent as IconPen } from "../../../styles/assets/svg/icon_pen.svg";
import IconMyProfile from "../../../styles/assets/img/icon_yellow.png";
import { PageContainer } from "src/components/organisms/contents";
import { useLogout } from "src/common/hooks/useLogout";
import {
  useDeleteUser,
  useUpdateUser,
  useUserRegister,
} from "src/common/hooks/useUserRegister";
import { formatBirthday } from "src/common/hooks/useFormatBirthDay";
import { showAlert } from "src/components/containers/Alert";
import { FallbackImage } from "src/pages/diet/DietPage";
import { useImgUpload } from "src/common/hooks/useImgRegister";
import { SubConHeader } from "src/components/atoms/SubHeaderBox/SubConHeader";

const SettingPage: React.FC = () => {
  const navi = useNavigate();
  const logOut = useLogout();
  const { px } = useViewport();

  const fileInputRef = useRef<HTMLInputElement | null>(null); // file ref
  const [targetSlideId, setTargetSlideId] = useState<string | null>(null);

  // 유저 정보 불러오기
  const { data: userInfo } = useUserRegister();

  /* 유저 정보 수정하기 */
  const { mutateAsync: updateUserInfo } = useUpdateUser();

  /* 유저 정보 탈퇴하기 */
  const { mutateAsync: deleteUser } = useDeleteUser();

  /* 이미지 업로드 */
  const { mutateAsync: uploadFile } = useImgUpload();

  const handleLogOut = async () => {
    const isConfirm = await confirmDialog({
      text: "로그아웃 하시겠습니까?",
      title: "로그아웃",
      options: {
        trueButtonText: "돌아가기",
        falseButtonText: "로그아웃",
      },
    });
    if (!isConfirm) {
      logOut();
    }
  };

  const handleDeleteUser = async () => {
    const isConfirm = await confirmDialog({
      text: "탈퇴 하시겠습니까?",
      title: "회원탈퇴",
      options: {
        trueButtonText: "돌아가기",
        falseButtonText: "탈퇴하기",
      },
    });
    if (!isConfirm) {
      deleteUser();
      logOut();
      navi("/auth/login");
    }
  };

  // 파일 선택 핸들러
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && targetSlideId) {
      if (file.type.startsWith("image/")) {
        const { response } = await uploadFile(file);
        await updateUserInfo({ profileImage: `/file/${response.oid}` });
      } else {
        showAlert("이미지 파일만 업로드할 수 있습니다."); // or toast
      }
      setTargetSlideId(null);
      e.target.value = ""; // 같은 파일 다시 선택 가능하게 초기화
    }
  };

  const genderRender = () => {
    if (userInfo?.gender === "M") return "남자";
    else return "여자";
  };

  return (
    <PageContainer isNavBar={false} active={null}>
      <>
        {/* 헤더 */}
        <SubConHeader
          title="내 정보 관리"
          onClickArrow={() => navi("/myinfo")}
        />

        <main
          style={{
            paddingTop: px(12),
            paddingBottom: px(100),
          }}
        >
          {/* 프로필 + 이름 */}
          <section
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: px(82),
                height: px(82),
                background: "#FAFAFA",
                borderRadius: 999,
                outline: `3px solid var(--neutral-200, #E5E5E5)`,
                outlineOffset: "-3px",
                position: "relative",
                display: "grid",
                placeItems: "center",
              }}
            >
              <div
                style={{
                  width: userInfo?.profileImage ? "100%" : "42px",
                  height: userInfo?.profileImage ? "100%" : "42px",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  fontSize: px(30),
                  boxShadow: "0 12px 14px rgba(0,0,0,0.08)",
                }}
              >
                {userInfo?.profileImage ? (
                  <FallbackImage
                    src={
                      userInfo.profileImage.startsWith("http")
                        ? userInfo.profileImage
                        : process.env.REACT_APP_API_FILE + userInfo.profileImage
                    }
                    alt={userInfo?.userName!}
                    profile={true}
                  />
                ) : (
                  <FallbackImage src={IconMyProfile} alt={"undifined"} />
                )}
              </div>
              <input
                type="file"
                accept="image/*" // img만 업로드 가능
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              {/* 카메라 아이콘 */}
              <button
                onClick={() => {
                  setTargetSlideId(userInfo?.oid || ""); // 클릭한 id 기억;
                  fileInputRef.current?.click(); // 파일창 열기;
                }}
                aria-label="프로필 사진 변경"
                style={{
                  position: "absolute",
                  right: px(-1), // 살짝 튀어나오게
                  bottom: px(-1),
                  width: px(28),
                  height: px(28),
                  borderRadius: 999,
                  background: "var(--neutral-700, #404040)",
                  border: `${px(2)}px solid #fff`, // 흰색 테두리로 피그마 느낌 강화
                  boxShadow: "0px 4px 11px rgba(119,119,119,0.10)",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                }}
              >
                <IconBox $isActive={false}>
                  <IconCamera />
                </IconBox>
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  color: "var(--neutral-800, #262626)",
                  fontSize: px(18),
                  fontWeight: 700,
                  lineHeight: `${px(28)}px`,
                }}
              >
                {userInfo?.userName}&nbsp;
              </span>
              <span
                style={{
                  color: "var(--neutral-800, #262626)",
                  fontSize: px(18),
                  fontWeight: 500,
                  lineHeight: `${px(28)}px`,
                }}
              >
                님
              </span>
            </div>
          </section>

          {/* 계정 정보 */}
          <section
            style={{ display: "grid", gap: px(10), marginBottom: "24px" }}
          >
            <div
              style={{
                color: "#000",
                fontSize: px(16),
                fontWeight: 700,
                lineHeight: `${px(24)}px`,
              }}
            >
              계정 정보
            </div>

            <div
              style={{
                background: "#FFFFFF",
                borderRadius: px(16),
                outline: `1px solid var(--neutral-200, #E5E5E5)`,
                outlineOffset: "-1px",
                display: "grid",
              }}
            >
              {userInfo && (
                <>
                  <RowTwoCol
                    left="이메일"
                    right={userInfo?.email || ""}
                    px={px}
                    leftColor="var(--neutral-500, #737373)"
                    rightColor="var(--neutral-700, #404040)"
                  />
                  {/* <Divider16 px={px} /> */}
                  <RowTwoCol
                    left="소셜 연동"
                    right={userInfo?.provider || "x"}
                    px={px}
                    leftColor="var(--neutral-500, #737373)"
                    rightColor="var(--neutral-700, #404040)"
                  />
                </>
              )}
            </div>
          </section>

          {/* 건강 정보 */}
          <section
            style={{ display: "grid", gap: px(10), marginBottom: "24px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: px(8) }}>
              <div
                style={{
                  color: "#000",
                  fontSize: px(16),
                  fontWeight: 700,
                  lineHeight: `${px(24)}px`,
                }}
              >
                건강 정보
              </div>

              <button
                onClick={() => navi("/mysetting/fixinfo")}
                aria-label="건강 정보 편집"
                style={{
                  width: px(18),
                  height: px(18),
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <IconPen />
              </button>
            </div>

            <div
              style={{
                background: "#FFFFFF",
                borderRadius: px(16),
                outline: `1px solid var(--neutral-200, #E5E5E5)`,
                outlineOffset: "-1px",
                display: "grid",
              }}
            >
              <RowTwoCol
                left="생년월일"
                right={
                  `${userInfo?.birthyear}.${formatBirthday(
                    userInfo?.birthday
                  )}` || ""
                }
                px={px}
                leftColor="var(--neutral-500, #737373)"
                rightColor="var(--neutral-700, #404040)"
              />
              {/* <Divider16 px={px} /> */}
              <RowTwoCol
                left="성별"
                right={genderRender()}
                px={px}
                leftColor="var(--neutral-500, #737373)"
                rightColor="var(--neutral-700, #404040)"
              />
              {/* <Divider16 px={px} /> */}
              <RowTwoCol
                left="키 · 몸무게"
                right={`${userInfo?.height}cm, ${userInfo?.weight}kg`}
                px={px}
                leftColor="var(--neutral-500, #737373)"
                rightColor="var(--neutral-700, #404040)"
              />
            </div>
          </section>

          {/* 액션 리스트 */}
          <section
            style={{
              background: "#FFFFFF",
              borderRadius: px(16),
              outline: `1px solid var(--neutral-200, #E5E5E5)`,
              outlineOffset: "-1px",
              overflow: "hidden",
            }}
          >
            <ListRow label="로그아웃" onClick={handleLogOut} px={px} />
            {/* <Divider16 px={px} $max={true} /> */}
            <ListRow label="회원탈퇴" onClick={handleDeleteUser} px={px} />
          </section>
        </main>

        {/* <NavBar active="myinfo" px={px} containerWidth={w} /> */}
      </>
    </PageContainer>
  );
};

/* ---------- 서브 컴포넌트---------- */
// const Divider16: React.FC<{ px: (n: number) => number; $max?: boolean }> = ({
//   px,
//   $max,
// }) => (
//   <div
//     style={{
//       height: px(1),
//       background: "var(--neutral-200, #E5E5E5)",
//       margin: $max ? "0" : `0 ${px(16)}px`,
//     }}
//   />
// );

const RowTwoCol: React.FC<{
  left: string;
  right: string;
  px: (n: number) => number;
  leftColor?: string;
  rightColor?: string;
}> = ({
  left,
  right,
  px,
  leftColor = "var(--neutral-500, #737373)",
  rightColor = "var(--neutral-700, #404040)",
}) => {
  // 소셜 조건 분기
  const renderRight = () => {
    if (right.toLowerCase() === "kakao") return "카카오";
    if (right.toLowerCase() === "naver") return "네이버";
    return right;
  };
  return (
    <div
      style={{
        height: px(52),
        padding: `0 ${px(16)}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: px(16),
      }}
    >
      <div
        style={{
          color: leftColor,
          fontSize: px(16),
          fontWeight: 500,
          lineHeight: `${px(24)}px`,
        }}
      >
        {left}
      </div>
      <div
        style={{
          color: rightColor,
          fontSize: px(16),
          fontWeight: 500,
          lineHeight: `${px(24)}px`,
        }}
      >
        {renderRight()}
      </div>
    </div>
  );
};

export default SettingPage;
